import React, { useState, useEffect } from 'react';
import { getAllDepreciation } from '../../../services/asset.service';
import Box from '@mui/material/Box';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarColumnsButton } from '@mui/x-data-grid';
import * as CryptoJS from "crypto-js";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import '../AssetInfo/AssetDepreciation.css';
import '../AssetInfo/AssetList.css';

const months = {
  '1': 'JAN', '2': 'FEB', '3': 'MAR', '4': 'APR', '5': 'MAY', '6': 'JUN',
  '7': 'JUL', '8': 'AUG', '9': 'SEP', '10': 'OCT', '11': 'NOV', '12': 'DEC'
};

/* ─── Helper: format number for display only (not for export) ─── */
const fmtINR = (val) =>
  val != null && val !== ''
    ? `Rs.${parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : 'Rs.0.00';

/* ─── Currency renderCell (display only — export stays as raw number) ─── */
const currencyCell = (params) => (
  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
    {fmtINR(params.value)}
  </span>
);

const columns = [
  { field: "id", hide: true, headerName: "Sl No" },
  { 
    field: "tag", hide: false, headerName: "Tag", width: 200,
    renderCell: (params) => (
      <div className="tag-cell">
        <i className="bi bi-tag-fill"></i>
        {params.value}
      </div>
    )
  },
  { 
    field: "parent_tag", hide: false, headerName: "Parent Tag", width: 200,
    renderCell: (params) => (
      <div className="parent-tag-cell">{params.value || '-'}</div>
    )
  },
  { field: "days", hide: false, headerName: "Days", width: 100, type: "number" },
  { field: "year", hide: false, headerName: "Year", width: 100 },
  { 
    field: "month", hide: false, headerName: "Month", width: 100,
    valueGetter: (params) => months[params.row.month],
    renderCell: (params) => <span className="month-badge">{params.value}</span>
  },

  /* ── Asset columns — renderCell for display; raw number exported ── */
  { field: "asset_ob",               hide: false, headerName: "Asset OB",          width: 160, type: "number", renderCell: currencyCell },
  { field: "asset_added_value",      hide: false, headerName: "Added Value",       width: 160, type: "number", renderCell: currencyCell, cellClassName: "positive-value" },
  { field: "asset_adjustment_value", hide: false, headerName: "Adjustment Value",  width: 160, type: "number", renderCell: currencyCell },
  { field: "asset_transfered",       hide: false, headerName: "Transferred",       width: 160, type: "number", renderCell: currencyCell },
  { field: "asset_deleted_value",    hide: false, headerName: "Deleted Value",     width: 160, type: "number", renderCell: currencyCell, cellClassName: "negative-value" },
  { field: "asset_cb",               hide: false, headerName: "Asset CB",          width: 160, type: "number", renderCell: currencyCell, cellClassName: "highlight-value" },

  /* ── Depreciation columns ── */
  { field: "depreciation_ob",              hide: false, headerName: "Dep. OB",          width: 160, type: "number", renderCell: currencyCell },
  { field: "depreciation_added_value",     hide: false, headerName: "Dep. Added",       width: 160, type: "number", renderCell: currencyCell, cellClassName: "positive-value" },
  { field: "depreciation_adjusted_value",  hide: false, headerName: "Dep. Adjusted",    width: 160, type: "number", renderCell: currencyCell },
  { field: "depreciation_transfered",      hide: false, headerName: "Dep. Transferred", width: 160, type: "number", renderCell: currencyCell },
  { field: "depreciation_deleted_value",   hide: false, headerName: "Dep. Deleted",     width: 160, type: "number", renderCell: currencyCell, cellClassName: "negative-value" },
  { field: "depreciation_cb",              hide: false, headerName: "Dep. CB",          width: 160, type: "number", renderCell: currencyCell, cellClassName: "highlight-value" },

  /* ── WDV columns ── */
  { field: "wdv_ob", hide: false, headerName: "WDV OB", width: 160, type: "number", renderCell: currencyCell },
  { field: "wdv_cb", hide: false, headerName: "WDV CB", width: 160, type: "number", renderCell: currencyCell, cellClassName: "highlight-value" },
];

function CustomToolbar() {
  return (
    <GridToolbarContainer className="custom-toolbar">
      <div className="toolbar-left">
        <GridToolbarColumnsButton className="toolbar-button" />
        <GridToolbarFilterButton className="toolbar-button" />
      </div>
      <div className="toolbar-right">
        <GridToolbarExport
          className="toolbar-button export-button"
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{
            fileName: 'Depreciation_Report',
            utf8WithBom: true,
          }}
        />
      </div>
    </GridToolbarContainer>
  );
}

function DepreciationInfo() {
  const [loading, setLoading] = useState(true);
  const [assetsDepriciation, setAssetsDepriciation] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // Filter states
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  
  const [stats, setStats] = useState({
    totalAssets: 0,
    totalAssetValue: 0,
    totalDepreciation: 0,
    totalWDV: 0
  });

  useEffect(() => {
    let EncryptData = CryptoJS.AES.encrypt(JSON.stringify({
      'authToken': sessionStorage.getItem('LoggedInToken'),
    }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();

    let oData = { 'token': EncryptData };

    getAllDepreciation(oData)
      .then((result) => {
        const data = result.data.data;
        if (data?.assetDeprisiation && data.assetDeprisiation.length > 0) {
          setAssetsDepriciation(data.assetDeprisiation);
          setFilteredData(data.assetDeprisiation);
          
          // Extract unique years for filter dropdown
          /*const years = [...new Set(data.assetDeprisiation.map(item => item.year))].sort((a, b) => b - a);
          setAvailableYears(years);*/
          const rawYears = [...new Set(data.assetDeprisiation.map(item => item.year))].sort((a, b) => b - a);
          setAvailableYears(rawYears);
          
          // Calculate statistics
          calculateStats(data.assetDeprisiation);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Recalculate stats whenever filtered data changes
  useEffect(() => {
    calculateStats(filteredData);
  }, [filteredData]);

  const calculateStats = (data) => {
    const totalAssets = data.length;
    const totalAssetValue = data.reduce((sum, item) => sum + (parseFloat(item.asset_cb) || 0), 0);
    const totalDepreciation = data.reduce((sum, item) => sum + (parseFloat(item.depreciation_cb) || 0), 0);
    const totalWDV = data.reduce((sum, item) => sum + (parseFloat(item.wdv_cb) || 0), 0);
    
    setStats({
      totalAssets,
      totalAssetValue,
      totalDepreciation,
      totalWDV
    });
  };

  const handleFilterApply = () => {
    let filtered = [...assetsDepriciation];

    if (selectedYear) {
      filtered = filtered.filter(item => String(item.year) === selectedYear);
    }

    if (selectedMonth) {
      filtered = filtered.filter(item => String(item.month) === selectedMonth);
    }

    setFilteredData(filtered);
  };

  const handleFilterReset = () => {
    setSelectedMonth('');
    setSelectedYear('');
    setFilteredData(assetsDepriciation);
  };

  const monthOptions = [
    { value: '4',  label: 'April' },
    { value: '5',  label: 'May' },
    { value: '6',  label: 'June' },
    { value: '7',  label: 'July' },
    { value: '8',  label: 'August' },
    { value: '9',  label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
    { value: '1',  label: 'January' },
    { value: '2',  label: 'February' },
    { value: '3',  label: 'March' },
  ];

  return (
    <div className='depreciation-info-container'>
      {/* Header Section */}
      <div className='al-page-header'>
        <div className='al-page-header-left'>
          <div className='al-page-header-icon'>
            <AccountBalanceIcon style={{ fontSize: '1.5rem' }} />
          </div>
          <div className='al-page-header-text'>
            <h5 className='al-page-title'>Asset Depreciation Report</h5>
            <p className='al-page-subtitle'>Comprehensive depreciation analysis (AS)</p>
          </div>
        </div>
        {!loading && assetsDepriciation.length > 0 && (
          <div className='al-record-badge'>
            <i className='bi bi-layers-fill'></i>
            <strong>{filteredData.length.toLocaleString('en-IN')}</strong>&nbsp;
            {selectedMonth || selectedYear ? 'Filtered' : 'Total'} Records
          </div>
        )}
      </div>

      {/* Month-Wise Filter Panel */}
      {!loading && assetsDepriciation.length > 0 && (
        <div className='al-filter-card'>
          <div className='al-filter-card-header'>
            <span className='al-filter-card-title'>
              <i className='bi bi-calendar3'></i> Month-Wise Filter
            </span>
          </div>
          <div className='al-filter-card-body'>
            <div className='al-filter-grid' style={{gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'}}>
              {/* ── Year first ── */}
              <div className='al-filter-group'>
                <label className='al-filter-label'>
                  <i className='bi bi-calendar-range'></i> Select Year
                </label>
                <select
                  className='al-form-select'
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <option value="">All Years</option>
                  {availableYears.map(year => (
                    <option key={year} value={String(year)}>
                      FY-{year}-{String(parseInt(year) + 1).slice(-2)}
                    </option>
                  ))}
                </select>
              </div>

              {/* ── Month second ── */}
              <div className='al-filter-group'>
                <label className='al-filter-label'>
                  <i className='bi bi-calendar-month'></i> Select Month
                </label>
                <select
                  className='al-form-select'
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <option value="">All Months</option>
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className='al-filter-actions'>
              <button className='al-btn al-btn-primary' onClick={handleFilterApply}>
                <i className='bi bi-funnel-fill'></i> Apply Filter
              </button>
              <button className='al-btn al-btn-outline' onClick={handleFilterReset}>
                <i className='bi bi-arrow-counterclockwise'></i> Reset Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {!loading && filteredData.length > 0 && (
        <div className='stats-grid'>
          <div className='stat-card card-blue'>
            <div className='stat-icon'>
              <i className='bi bi-boxes'></i>
            </div>
            <div className='stat-content'>
              <div className='stat-label'>Total Assets</div>
              <div className='stat-value stat-value-wrap'>{stats.totalAssets.toLocaleString('en-IN')}</div>
            </div>
          </div>

          <div className='stat-card card-green'>
            <div className='stat-icon'>
              <TrendingUpIcon />
            </div>
            <div className='stat-content'>
              <div className='stat-label'>Total Asset Value (CB)</div>
              <div className='stat-value stat-value-wrap'>Rs.{stats.totalAssetValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div className='stat-card card-orange'>
            <div className='stat-icon'>
              <TrendingDownIcon />
            </div>
            <div className='stat-content'>
              <div className='stat-label'>Total Depreciation (CB)</div>
              <div className='stat-value stat-value-wrap'>Rs.{stats.totalDepreciation.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div className='stat-card card-purple'>
            <div className='stat-icon'>
              <AttachMoneyIcon />
            </div>
            <div className='stat-content'>
              <div className='stat-label'>Total WDV (CB)</div>
              <div className='stat-value stat-value-wrap'>Rs.{stats.totalWDV.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>
      )}

      {/* Data Grid Section */}
      <div className='data-grid-section'>
        {loading ? (
          <div className="loading-state">
            <div className="spinner-container">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="loading-text">Loading depreciation data...</p>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-inbox" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
            <h5>No Depreciation Data Available</h5>
            <p>
              {selectedMonth || selectedYear 
                ? 'No records found for the selected filters. Try adjusting your filter criteria.'
                : 'There are no depreciation records to display at this time.'}
            </p>
          </div>
        ) : (
          <Box className="data-grid-box">
            <DataGrid
              rows={filteredData}
              getRowId={(row) => row.id}
              columns={columns}
              autoHeight
              slots={{ toolbar: CustomToolbar }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              className="custom-data-grid"
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                 color: '#000', fontSize: '0.88rem',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.82rem',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#eef7fd',
                },
              }}
            />
          </Box>
        )}
      </div>
    </div>
  );
}

export default DepreciationInfo;